import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck, Calendar, DollarSign, Loader2, LogOut, Mail, Music, Radio,
  Search, TrendingUp, UserPlus, Users, Zap, Activity, MessageSquare, Shield,
  Trash2, UserX, UserCheck, ChevronRight, Eye, Ticket,
} from "lucide-react";
import { adminFetch, TOKEN_KEY } from "../lib/adminApi";
import { ReportingBaselineCard } from "../components/admin/ReportingBaselineCard";
import { CouponsTab } from "../components/admin/CouponsTab";

const usd = (cents) =>
  "$" + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: cents % 100 ? 2 : 0 });

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

const fmtDate = (d) => d.toISOString().slice(0, 10);

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "all", label: "All Time" },
];

function rangeParams(preset, customFrom, customTo) {
  const now = new Date();
  if (preset === "custom") return { from: customFrom, to: customTo };
  if (preset === "today") return { from: fmtDate(now), to: fmtDate(now) };
  if (preset === "7d") return { from: fmtDate(new Date(now - 7 * 864e5)), to: fmtDate(now) };
  if (preset === "all") return {};
  return { from: fmtDate(new Date(now - 30 * 864e5)), to: fmtDate(now) };
}

function qs(params) {
  const p = new URLSearchParams(Object.entries(params).filter(([, v]) => v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

// ---------------------------------------------------------------- Overview
function StatCard({ testid, icon: Icon, label, value, sub, accent = "text-gold" }) {
  return (
    <div data-testid={testid} className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${accent}`} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
      </div>
      <p className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function OverviewTab({ params }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null);
    adminFetch(`/api/admin/overview${qs(params)}`)
      .then(setData)
      .catch((e) => setErr(e.message));
  }, [params]);
  if (err) return <p className="text-coral text-sm">{err}</p>;
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-gold mx-auto mt-16" />;

  const { funnel, costs } = data;
  const maxRev = Math.max(1, ...data.daily.map((d) => d.revenue_cents));
  const funnelRows = [
    ["Page views", funnel.pageviews],
    ["Unique visitors", funnel.unique_visitors],
    ["Landing page views", funnel.landing_views],
    ["Studio sessions", funnel.studio_views],
    ["Preview listens", funnel.preview_views],
    ["Previews generated", funnel.jobs_started],
    ["Orders", funnel.orders],
  ];
  const maxFunnel = Math.max(1, ...funnelRows.map(([, v]) => v));

  return (
    <div className="space-y-8">
      {data.baseline && (
        <p data-testid="overview-baseline-note" className="text-xs text-slate-500 -mt-2">
          Reports start from <span className="font-mono text-gold">{fmtTs(data.baseline)}</span> — earlier test data is hidden.
        </p>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard testid="stat-revenue" icon={DollarSign} label="Revenue" value={usd(data.revenue_cents)}
                  sub={`${data.orders} orders · AOV ${usd(data.aov_cents)}`} />
        <StatCard testid="stat-customers" icon={BadgeCheck} label="New Customers" value={data.new_customers}
                  sub="paid sites in range" accent="text-neon" />
        <StatCard testid="stat-visitors" icon={Users} label="Unique Visitors" value={funnel.unique_visitors}
                  sub={`${funnel.pageviews} page views`} accent="text-neon" />
        <StatCard testid="stat-conversion" icon={TrendingUp} label="Visitor → Order"
                  value={funnel.visitor_to_order === null ? "—" : `${funnel.visitor_to_order}%`}
                  sub="needs page-view data to mature" accent="text-coral" />
        <StatCard testid="stat-ai-cost" icon={Zap} label="Est. AI Cost" value={`$${costs.total_usd}`}
                  sub={`${costs.suno_renders} renders · ${costs.claude_calls} Claude · ${costs.whisper_qc} QC`} />
        <StatCard testid="stat-cost-per-order" icon={Activity} label="Cost / Order"
                  value={data.cost_per_order_usd === null ? "—" : `$${data.cost_per_order_usd}`}
                  sub="estimated, all-in AI" accent="text-coral" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Revenue by day</p>
          <div data-testid="daily-chart" className="flex items-end gap-1 h-32">
            {data.daily.length === 0 && <p className="text-xs text-slate-600">No sales in this range yet.</p>}
            {data.daily.map((d) => (
              <div key={d.date} className="flex-1 group relative" title={`${d.date}: ${usd(d.revenue_cents)} · ${d.orders} orders · ${d.visitors} visitors`}>
                <div className="bg-gold/70 group-hover:bg-gold rounded-t transition-colors w-full"
                     style={{ height: `${Math.max(2, (d.revenue_cents / maxRev) * 120)}px` }} />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Conversion funnel</p>
          <div data-testid="funnel" className="space-y-2.5">
            {funnelRows.map(([label, v]) => (
              <div key={label} className="flex items-center gap-3">
                <span className="text-xs text-slate-400 w-36 shrink-0">{label}</span>
                <div className="flex-1 h-5 bg-obsidian rounded overflow-hidden">
                  <div className="h-full bg-neon/60 rounded" style={{ width: `${(v / maxFunnel) * 100}%` }} />
                </div>
                <span className="text-xs font-mono text-slate-300 w-14 text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Sales by product</p>
          <table data-testid="sales-by-product" className="w-full text-sm">
            <tbody>
              {data.by_product.length === 0 && (
                <tr><td className="text-xs text-slate-600 py-2">No sales in this range.</td></tr>
              )}
              {data.by_product.map((p) => (
                <tr key={p.lookup_key} className="border-t border-[#1d2030]">
                  <td className="py-2.5 text-slate-300">{p.label}</td>
                  <td className="py-2.5 text-right font-mono text-slate-500">{p.count}×</td>
                  <td className="py-2.5 text-right font-mono text-gold">{usd(p.cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Estimated AI usage cost</p>
          <p className="text-[10px] text-slate-600 mb-4">{costs.note}</p>
          <table data-testid="cost-breakdown" className="w-full text-sm">
            <tbody>
              <CostRow label={`Suno renders (Kie V5)`} qty={`${costs.suno_renders}×`} usdVal={costs.suno_usd} />
              <CostRow label="Claude creative calls" qty={`${costs.claude_calls}×`} usdVal={costs.claude_usd} />
              <CostRow label="Whisper QC passes" qty={`${costs.whisper_qc}×`} usdVal={costs.whisper_usd} />
              <CostRow label="Voice-over TTS" qty={`${costs.tts_chars} chars`} usdVal={costs.tts_usd} />
              <tr className="border-t border-[#262a3d]">
                <td className="py-2.5 font-bold text-white">Total</td>
                <td />
                <td className="py-2.5 text-right font-mono font-bold text-coral">${costs.total_usd}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Recent activity</p>
        <ul data-testid="activity-feed" className="space-y-2">
          {data.activity.length === 0 && <li className="text-xs text-slate-600">Nothing in this range yet.</li>}
          {data.activity.map((e, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className={`h-2 w-2 rounded-full shrink-0 ${e.kind === "sale" ? "bg-gold" : e.kind === "lead" ? "bg-neon" : "bg-slate-500"}`} />
              <span className="text-slate-300 flex-1">{e.label}</span>
              <span className="text-[10px] font-mono text-slate-600">{fmtTs(e.ts)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function CostRow({ label, qty, usdVal }) {
  return (
    <tr className="border-t border-[#1d2030]">
      <td className="py-2.5 text-slate-300">{label}</td>
      <td className="py-2.5 text-right font-mono text-slate-500">{qty}</td>
      <td className="py-2.5 text-right font-mono text-slate-300">${usdVal}</td>
    </tr>
  );
}

// ---------------------------------------------------------------- CRM
function CrmTab() {
  const [q, setQ] = useState("");
  const [contacts, setContacts] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback((query) => {
    adminFetch(`/api/admin/contacts${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then(setContacts).catch(() => setContacts([]));
  }, []);

  useEffect(() => { load(""); }, [load]);

  const openContact = (email) => {
    setSelected(email);
    setDetail(null);
    adminFetch(`/api/admin/contacts/${encodeURIComponent(email)}`)
      .then(setDetail).catch(() => setDetail({ error: true }));
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <div className="relative mb-4">
          <Search className="h-4 w-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            data-testid="crm-search"
            value={q}
            onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
            placeholder="Search email, name, business…"
            className="w-full bg-[#12141D] border border-[#262a3d] rounded-xl pl-10 pr-4 py-3 text-sm outline-none focus:border-gold/60"
          />
        </div>
        <div data-testid="crm-contact-list" className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {!contacts && <Loader2 className="h-5 w-5 animate-spin text-gold mx-auto mt-8" />}
          {contacts?.length === 0 && <p className="text-xs text-slate-600">No contacts match.</p>}
          {contacts?.map((c) => (
            <button
              key={c.email}
              data-testid={`crm-contact-${c.email}`}
              onClick={() => openContact(c.email)}
              className={`w-full text-left bg-[#12141D] border rounded-xl px-4 py-3 transition-colors cursor-pointer ${
                selected === c.email ? "border-gold/60" : "border-[#262a3d] hover:border-slate-600"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-white truncate">{c.business_name || c.name || c.email}</span>
                <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border shrink-0 ${
                  c.paid ? "text-gold border-gold/40 bg-gold/10" : "text-slate-400 border-slate-600"}`}>
                  {c.paid ? "customer" : "prospect"}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-slate-500 truncate">{c.email}</span>
                <span className="text-xs font-mono text-slate-400 shrink-0">{c.spent_cents ? usd(c.spent_cents) : ""}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-3">
        {!selected && (
          <div className="bg-[#12141D] border border-dashed border-[#262a3d] rounded-2xl p-10 text-center text-slate-600 text-sm">
            Pick a contact to see their full history.
          </div>
        )}
        {selected && !detail && <Loader2 className="h-5 w-5 animate-spin text-gold mx-auto mt-8" />}
        {detail && !detail.error && (
          <div data-testid="crm-contact-detail" className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5 space-y-5">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-xl font-bold">{detail.business_name || detail.name || detail.email}</h3>
                {detail.paid && (
                  <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border text-gold border-gold/40 bg-gold/10">customer</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">{detail.email}{detail.phone ? ` · ${detail.phone}` : ""}</p>
              <p className="text-xs text-slate-500 mt-1">
                {detail.site_keys.join(", ") || "no site"} · lifetime value <span className="text-gold font-mono">{usd(detail.spent_cents)}</span>
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Activity history</p>
              <ul data-testid="crm-timeline" className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {detail.timeline.length === 0 && <li className="text-xs text-slate-600">No activity recorded yet.</li>}
                {detail.timeline.map((e, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${
                      e.kind === "sale" ? "bg-gold" : e.kind === "email" ? "bg-neon" : e.kind === "lead" ? "bg-coral" : "bg-slate-500"}`} />
                    <span className="text-slate-300 flex-1">{e.label}</span>
                    <span className="text-[10px] font-mono text-slate-600">{fmtTs(e.ts)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">
                Communications ({detail.emails.length})
              </p>
              <ul className="space-y-1.5">
                {detail.emails.length === 0 && <li className="text-xs text-slate-600">No emails logged yet — logging starts with the next send.</li>}
                {detail.emails.map((e, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-xs">
                    <Mail className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                    <span className="text-slate-300 flex-1 truncate">{e.subject}</span>
                    <span className={`font-mono ${e.status === "sent" ? "text-neon" : "text-coral"}`}>{e.status}</span>
                    <span className="font-mono text-slate-600">{fmtTs(e.sent_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Conversations
function ConversationsTab() {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState(null);

  useEffect(() => {
    adminFetch("/api/admin/conversations").then(setThreads).catch(() => setThreads([]));
  }, []);

  const openThread = (email) => {
    setActive(email);
    setMessages(null);
    adminFetch(`/api/admin/conversations/${encodeURIComponent(email)}`)
      .then(setMessages).catch(() => setMessages([]));
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-2 max-h-[65vh] overflow-y-auto pr-1" data-testid="conversation-list">
        {!threads && <Loader2 className="h-5 w-5 animate-spin text-gold mx-auto mt-8" />}
        {threads?.length === 0 && (
          <div className="bg-[#12141D] border border-dashed border-[#262a3d] rounded-2xl p-8 text-center">
            <MessageSquare className="h-6 w-6 text-slate-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500">No conversations yet.</p>
            <p className="text-[10px] text-slate-600 mt-1">Every outgoing email is logged from the next send onward.</p>
          </div>
        )}
        {threads?.map((t) => (
          <button
            key={t.email}
            data-testid={`thread-${t.email}`}
            onClick={() => openThread(t.email)}
            className={`w-full text-left bg-[#12141D] border rounded-xl px-4 py-3 transition-colors cursor-pointer ${
              active === t.email ? "border-gold/60" : "border-[#262a3d] hover:border-slate-600"}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-bold truncate">{t.business_name || t.name || t.email}</span>
              <span className="text-[10px] font-mono text-slate-600 shrink-0">{t.count}</span>
            </div>
            <p className="text-xs text-slate-500 truncate mt-0.5">{t.last_subject}</p>
            <p className="text-[10px] font-mono text-slate-600 mt-0.5">{fmtTs(t.last_sent)}</p>
          </button>
        ))}
      </div>

      <div className="lg:col-span-3">
        {!active && (
          <div className="bg-[#12141D] border border-dashed border-[#262a3d] rounded-2xl p-10 text-center text-slate-600 text-sm">
            Pick a conversation to read it.
          </div>
        )}
        {active && !messages && <Loader2 className="h-5 w-5 animate-spin text-gold mx-auto mt-8" />}
        {messages && (
          <div data-testid="conversation-thread" className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {messages.length === 0 && <p className="text-xs text-slate-600">No messages logged for this contact yet.</p>}
            {messages.map((m, i) => (
              <div key={i} className="bg-[#12141D] border border-[#262a3d] rounded-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#1d2030]">
                  <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-neon/10 border border-neon/30 text-neon">
                    sent by us
                  </span>
                  <span className="text-xs font-bold text-slate-200 truncate flex-1">{m.subject}</span>
                  <span className="text-[10px] font-mono text-slate-600 shrink-0">{fmtTs(m.sent_at)}</span>
                </div>
                <iframe
                  title={`email-${i}`}
                  sandbox=""
                  srcDoc={m.html || ""}
                  className="w-full h-80 bg-[#0A0B10]"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Admins
function AdminsTab({ me }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "admin" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const isSuper = me?.role === "super_admin";

  const load = useCallback(() => {
    adminFetch("/api/admin/users").then(setUsers).catch(() => setUsers([]));
  }, []);
  useEffect(load, [load]);

  const addUser = async (e) => {
    e.preventDefault();
    setMsg(""); setErr("");
    try {
      await adminFetch("/api/admin/users", { method: "POST", body: JSON.stringify(form) });
      setMsg(`Added ${form.email}`);
      setForm({ email: "", name: "", password: "", role: "admin" });
      load();
    } catch (e2) { setErr(e2.message); }
  };

  const patch = async (id, body) => {
    setMsg(""); setErr("");
    try {
      await adminFetch(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      load();
    } catch (e2) { setErr(e2.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} permanently? They will lose all access.`)) return;
    setMsg(""); setErr("");
    try {
      await adminFetch(`/api/admin/users/${u.id}`, { method: "DELETE" });
      setMsg(`Removed ${u.email}`);
      load();
    } catch (e2) { setErr(e2.message); }
  };

  return (
    <div className="space-y-6">
      <ReportingBaselineCard isSuper={isSuper} />

      <div className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Admin users</p>
        <div data-testid="admin-user-list" className="space-y-2">
          {!users && <Loader2 className="h-5 w-5 animate-spin text-gold mx-auto" />}
          {users?.map((u) => (
            <div key={u.id} className="flex items-center gap-3 flex-wrap border border-[#1d2030] rounded-xl px-4 py-3">
              <div className="flex-1 min-w-40">
                <p className="text-sm font-bold">{u.name || u.email} {u.id === me?.id && <span className="text-slate-500 font-normal">(you)</span>}</p>
                <p className="text-xs text-slate-500">{u.email} · last login {fmtTs(u.last_login_at)}</p>
              </div>
              <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                u.role === "super_admin" ? "text-gold border-gold/40 bg-gold/10" : "text-slate-400 border-slate-600"}`}>
                {u.role === "super_admin" ? "super admin" : "admin"}
              </span>
              <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                u.active ? "text-neon border-neon/40 bg-neon/10" : "text-coral border-coral/40 bg-coral/10"}`}>
                {u.active ? "active" : "deactivated"}
              </span>
              {isSuper && u.id !== me?.id && (
                <div className="flex gap-2">
                  <button
                    data-testid={`admin-toggle-${u.email}`}
                    onClick={() => patch(u.id, { active: !u.active })}
                    className="text-slate-400 hover:text-white transition-colors cursor-pointer"
                    title={u.active ? "Deactivate (keeps history, blocks login)" : "Reactivate"}
                  >
                    {u.active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                  </button>
                  <button
                    data-testid={`admin-remove-${u.email}`}
                    onClick={() => remove(u)}
                    className="text-slate-400 hover:text-coral transition-colors cursor-pointer"
                    title="Remove permanently"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {isSuper && (
        <form data-testid="admin-add-form" onSubmit={addUser} className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Add an admin</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="admin-add-email" type="email" required placeholder="Email"
                   value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="bg-obsidian border border-[#262a3d] rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/60" />
            <input data-testid="admin-add-name" placeholder="Name (optional)"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="bg-obsidian border border-[#262a3d] rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/60" />
            <input data-testid="admin-add-password" type="password" required minLength={8} placeholder="Password (min 8 chars)"
                   value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                   className="bg-obsidian border border-[#262a3d] rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/60" />
            <select data-testid="admin-add-role" value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="bg-obsidian border border-[#262a3d] rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/60 text-slate-300">
              <option value="admin">Admin</option>
              <option value="super_admin">Super admin</option>
            </select>
          </div>
          <button data-testid="admin-add-submit" type="submit"
                  className="mt-4 bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold px-6 py-3 rounded-xl text-sm hover:scale-[1.01] transition-transform cursor-pointer flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> ADD ADMIN
          </button>
        </form>
      )}
      {msg && <p data-testid="admin-action-success" className="text-neon text-sm">{msg}</p>}
      {err && <p data-testid="admin-action-error" className="text-coral text-sm">{err}</p>}
      {!isSuper && (
        <p className="text-xs text-slate-600 flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5" /> Only super admins can add, deactivate, or remove admins.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- shell
const TABS = [
  { key: "overview", label: "Overview", icon: TrendingUp },
  { key: "crm", label: "CRM", icon: Users },
  { key: "conversations", label: "Conversations", icon: MessageSquare },
  { key: "coupons", label: "Coupons", icon: Ticket },
  { key: "admins", label: "Admins", icon: Shield },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [preset, setPreset] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      navigate("/admin-login");
      return;
    }
    adminFetch("/api/admin/auth/me").then(setMe).catch(() => {});
  }, [navigate]);

  const params = useMemo(() => rangeParams(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("lbj_admin");
    navigate("/admin-login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-obsidian text-white">
      <header className="border-b border-[#1d2030] px-5 sm:px-8 py-4 flex items-center gap-4">
        <span className="h-9 w-9 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
          <Radio className="h-4 w-4 text-gold" />
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-lg font-bold tracking-tight">Studio <span className="text-gold">Control</span></h1>
          <p className="text-[10px] font-mono text-slate-600 truncate">{me?.email || "…"}</p>
        </div>
        <button data-testid="admin-logout" onClick={logout}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors cursor-pointer">
          <LogOut className="h-3.5 w-3.5" /> Log out
        </button>
      </header>

      <nav className="px-5 sm:px-8 pt-5 flex items-center gap-2 flex-wrap">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors cursor-pointer border ${
              tab === key
                ? "bg-gold/10 border-gold/50 text-gold"
                : "border-[#262a3d] text-slate-400 hover:text-white"}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>

      {tab === "overview" && (
        <div className="px-5 sm:px-8 pt-5 flex items-center gap-2 flex-wrap" data-testid="date-range-bar">
          <Calendar className="h-4 w-4 text-slate-500" />
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`date-preset-${p.key}`}
              onClick={() => setPreset(p.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors cursor-pointer border ${
                preset === p.key ? "bg-neon/10 border-neon/50 text-neon" : "border-[#262a3d] text-slate-400 hover:text-white"}`}
            >
              {p.label}
            </button>
          ))}
          <button
            data-testid="date-preset-custom"
            onClick={() => setPreset("custom")}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors cursor-pointer border ${
              preset === "custom" ? "bg-neon/10 border-neon/50 text-neon" : "border-[#262a3d] text-slate-400 hover:text-white"}`}
          >
            Custom
          </button>
          {preset === "custom" && (
            <span className="flex items-center gap-2">
              <input data-testid="date-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                     className="bg-[#12141D] border border-[#262a3d] rounded-lg px-2.5 py-1.5 text-xs text-slate-300 outline-none" />
              <span className="text-slate-600 text-xs">→</span>
              <input data-testid="date-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                     className="bg-[#12141D] border border-[#262a3d] rounded-lg px-2.5 py-1.5 text-xs text-slate-300 outline-none" />
            </span>
          )}
        </div>
      )}

      <main className="px-5 sm:px-8 py-6 max-w-7xl">
        {tab === "overview" && <OverviewTab params={params} />}
        {tab === "crm" && <CrmTab />}
        {tab === "conversations" && <ConversationsTab />}
        {tab === "coupons" && <CouponsTab />}
        {tab === "admins" && <AdminsTab me={me} />}
      </main>
    </div>
  );
}
